// Floating overlay card shown on the Overview map when the user
// clicks a pin. Renders as an absolute-positioned overlay in the
// bottom-left of the map area, with a clean light header, smart
// data visualisations, and tight polished spacing.

import {
  Box, CircularProgress, IconButton, Stack, Tooltip,
  Typography, alpha,
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined'
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded'
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded'
import RuleRoundedIcon from '@mui/icons-material/RuleRounded'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded'
import { useQuery } from '@tanstack/react-query'
import { Link as RouterLink } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'

import { api } from '../../../lib/api'
import type { SiteOverviewData } from '@cmms/api-client'
import { UserAvatar } from '../../../components/UserAvatar'

interface Props {
  siteId: string | null
  onClose: () => void
}

// Status palette — kept calm so the stacked bar reads as a single
// composition, not four loud blocks.
const WO_STATUS = [
  { key: 'open',        label: 'Open',     color: '#F59E0B' },
  { key: 'in_progress', label: 'Active',   color: '#3B82F6' },
  { key: 'resolved',    label: 'Resolved', color: '#8B5CF6' },
  { key: 'closed',      label: 'Closed',   color: '#10B981' },
] as const

export function SiteOverviewPanel({ siteId, onClose }: Props) {
  const q = useQuery({
    queryKey: ['analytics', 'site-overview', siteId],
    queryFn: () => api.analytics.siteOverview(siteId!),
    enabled: Boolean(siteId),
    staleTime: 60_000,
  })

  return (
    <AnimatePresence>
      {siteId && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, x: -18, y: 18, scale: 0.97 }}
          animate={{ opacity: 1, x: 0,   y: 0,  scale: 1    }}
          exit={{    opacity: 0, x: -10, y: 10, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 320, damping: 28 }}
          sx={{
            position: 'absolute',
            left: 12, bottom: 12,
            zIndex: 600,
            width: { xs: 'calc(100% - 24px)', sm: 340 },
            maxHeight: 'calc(100% - 24px)',
            display: 'flex', flexDirection: 'column',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: (t) => alpha(t.palette.primary.main, 0.18),
            borderRadius: 3,
            boxShadow: (t) => t.palette.mode === 'light'
              ? `0 22px 50px -16px rgba(15, 23, 42, 0.45), 0 6px 14px -6px rgba(15, 23, 42, 0.18)`
              : `0 22px 50px -16px rgba(0, 0, 0, 0.75), 0 6px 14px -6px rgba(0, 0, 0, 0.55)`,
            overflow: 'hidden',
          }}
        >
          {q.isLoading || !q.data ? (
            <Box sx={{ p: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <PanelBody data={q.data} onClose={onClose} />
          )}
        </Box>
      )}
    </AnimatePresence>
  )
}

function PanelBody({
  data, onClose,
}: { data: SiteOverviewData; onClose: () => void }) {
  const { site, counts, checklists, top_performers } = data
  const wo = counts.work_orders

  return (
    <>
      {/* ── Header — clean, light, one accent strip ─────────────── */}
      <Box sx={{
        position: 'relative', flexShrink: 0,
        px: 2, pt: 1.75, pb: 1.5,
        bgcolor: 'background.paper',
        // Crisp coloured top edge — communicates identity without
        // drowning the card in primary blue.
        borderTop: '3px solid',
        borderTopColor: 'primary.main',
      }}>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute', top: 8, right: 8,
            width: 26, height: 26,
            color: 'text.secondary',
            bgcolor: (t) => alpha(t.palette.text.primary, 0.05),
            '&:hover': { bgcolor: (t) => alpha(t.palette.text.primary, 0.1), color: 'text.primary' },
          }}>
          <CloseRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ pr: 3.5 }}>
          <Box sx={{
            width: 34, height: 34, borderRadius: 1.5, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
            color: 'primary.contrastText',
            boxShadow: (t) => `0 4px 12px -3px ${alpha(t.palette.primary.main, 0.45)}`,
          }}>
            <HomeWorkOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{
              fontSize: 9, fontWeight: 800, letterSpacing: 0.8,
              textTransform: 'uppercase', color: 'primary.main', lineHeight: 1.1,
            }}>
              Site overview
            </Typography>
            <Typography sx={{
              fontSize: 15, fontWeight: 800, lineHeight: 1.3,
              color: 'text.primary',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {site.name}
            </Typography>
          </Box>
        </Stack>
        {site.address && (
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ mt: 0.65 }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
            <Typography sx={{
              fontSize: 11, color: 'text.secondary', lineHeight: 1.35,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {site.address}
            </Typography>
          </Stack>
        )}
        {(site.entity || site.state || site.category) && (
          <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap sx={{ mt: 0.85 }}>
            {site.entity   && <HeaderTag label={site.entity}   tone="primary" />}
            {site.state    && <HeaderTag label={site.state}    tone="muted" />}
            {site.category && <HeaderTag label={site.category} tone="muted" />}
          </Stack>
        )}
      </Box>

      {/* ── Body (scrollable) ─────────────────────────────────── */}
      <Box sx={{
        flex: 1, overflowY: 'auto', minHeight: 0,
        px: 1.5, pt: 1.25, pb: 1.5,
        borderTop: '1px solid', borderColor: 'divider',
        scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
        '&:hover::-webkit-scrollbar-thumb': {
          background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.18)',
        },
      }}>
        {/* Quick stat row -------------------------------------- */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 0.75, mb: 1.5,
        }}>
          <StatTile icon={<LocationCityOutlinedIcon sx={{ fontSize: 13 }} />}
            label="Locations" value={counts.locations} accent="#0D9488" />
          <StatTile icon={<PrecisionManufacturingOutlinedIcon sx={{ fontSize: 13 }} />}
            label="Assets" value={counts.assets} accent="#0EA5E9"
            sub={counts.assets_critical > 0 ? `${counts.assets_critical} critical` : undefined}
            subTone="#DC2626" />
          <StatTile icon={<Inventory2OutlinedIcon sx={{ fontSize: 13 }} />}
            label="Inventory" value={counts.inventory} accent="#8B5CF6"
            sub={counts.inventory_low_stock > 0 ? `${counts.inventory_low_stock} low` : undefined}
            subTone="#DC2626" />
          <StatTile icon={<GroupsOutlinedIcon sx={{ fontSize: 13 }} />}
            label="People" value={counts.people} accent="#F59E0B" />
        </Box>

        {/* Work orders — stacked bar instead of 4 boxes ---------- */}
        <SectionRow
          icon={<AssignmentOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />}
          title="Work orders"
          right={
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                {wo.total}
              </Typography>
              <Tooltip title="Open full list" arrow>
                <IconButton component={RouterLink} to={`/work-orders?site_id=${site.id}`}
                  size="small"
                  sx={{ width: 18, height: 18, color: 'text.disabled',
                    '&:hover': { color: 'primary.main' } }}>
                  <OpenInNewRoundedIcon sx={{ fontSize: 11 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          }
        />
        {wo.total === 0 ? (
          <InlineEmpty icon={<HourglassEmptyRoundedIcon sx={{ fontSize: 14 }} />}
            text="No work orders yet" />
        ) : (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{
              display: 'flex', height: 8, borderRadius: 99, overflow: 'hidden',
              bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
              mb: 0.85,
            }}>
              {WO_STATUS.map((s) => {
                const value = wo[s.key]
                if (value === 0) return null
                const pct = (value / wo.total) * 100
                return (
                  <Tooltip key={s.key}
                    title={`${s.label}: ${value} (${Math.round(pct)}%)`} arrow>
                    <Box
                      component={motion.div}
                      whileHover={{ filter: 'brightness(1.1)' }}
                      sx={{
                        width: `${pct}%`,
                        bgcolor: s.color,
                        transition: 'width 220ms ease',
                      }}
                    />
                  </Tooltip>
                )
              })}
            </Box>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {WO_STATUS.map((s) => (
                <Stack key={s.key} direction="row" alignItems="center" spacing={0.4}
                  sx={{ pr: 0.75 }}>
                  <Box sx={{
                    width: 6, height: 6, borderRadius: '50%', bgcolor: s.color,
                    boxShadow: `0 0 0 2px ${alpha(s.color, 0.2)}`,
                  }} />
                  <Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 600 }}>
                    {s.label}
                  </Typography>
                  <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'text.primary' }}>
                    {wo[s.key]}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        {/* Checklists — inline pct + ring -------------------- */}
        <SectionRow
          icon={<RuleRoundedIcon sx={{ fontSize: 13, color: 'success.main' }} />}
          title="Checklists"
          right={checklists.total > 0 ? (
            <Typography sx={{ fontSize: 11, color: 'text.disabled', fontWeight: 600 }}>
              {checklists.submitted}/{checklists.total}
            </Typography>
          ) : undefined}
        />
        {checklists.total === 0 ? (
          <InlineEmpty icon={<RuleRoundedIcon sx={{ fontSize: 14 }} />}
            text="No checklists assigned" />
        ) : (
          <ChecklistRing pct={checklists.completion_rate ?? 0} />
        )}

        {/* Top performers ---------------------------------------- */}
        <SectionRow
          icon={<EmojiEventsRoundedIcon sx={{ fontSize: 14, color: '#F59E0B' }} />}
          title="Top performers"
        />
        {top_performers.length === 0 ? (
          <InlineEmpty icon={<EmojiEventsRoundedIcon sx={{ fontSize: 14 }} />}
            text="No completed work yet" />
        ) : (
          <Stack spacing={0.25} sx={{ mt: 0.25 }}>
            {top_performers.map((p, i) => (
              <PerformerRow key={p.user_id} performer={p} rank={i + 1} />
            ))}
          </Stack>
        )}
      </Box>
    </>
  )
}

// ── Sub-components ──────────────────────────────────────────────────

function HeaderTag({ label, tone }: { label: string; tone: 'primary' | 'muted' }) {
  return (
    <Box sx={{
      px: 0.85, py: 0.2, borderRadius: 0.85,
      fontSize: 10, fontWeight: 700, lineHeight: 1.35,
      bgcolor: (t) => tone === 'primary'
        ? alpha(t.palette.primary.main, 0.1)
        : alpha(t.palette.text.primary, 0.05),
      color: tone === 'primary' ? 'primary.main' : 'text.secondary',
      border: '1px solid',
      borderColor: (t) => tone === 'primary'
        ? alpha(t.palette.primary.main, 0.2)
        : 'divider',
      maxWidth: 140,
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }}>
      {label}
    </Box>
  )
}

interface StatTileProps {
  icon: React.ReactNode
  label: string
  value: number
  accent: string
  sub?: string
  subTone?: string
}
function StatTile({ icon, label, value, accent, sub, subTone }: StatTileProps) {
  return (
    <Box
      component={motion.div}
      whileHover={{ y: -2 }}
      transition={{ type: 'spring', stiffness: 360, damping: 22 }}
      sx={{
        position: 'relative',
        p: 1, pt: 1.25, borderRadius: 1.5,
        bgcolor: 'background.paper',
        border: '1px solid', borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {/* Coloured accent strip */}
      <Box sx={{
        position: 'absolute', left: 0, top: 0, width: 3, height: '100%',
        bgcolor: accent,
      }} />
      <Stack direction="row" alignItems="center" spacing={0.4} sx={{ mb: 0.25 }}>
        <Box sx={{ color: accent, display: 'inline-flex' }}>{icon}</Box>
        <Typography sx={{
          fontSize: 9, fontWeight: 800, letterSpacing: 0.4,
          textTransform: 'uppercase', color: 'text.disabled', lineHeight: 1.1,
        }}>
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1, color: 'text.primary' }}>
        {value.toLocaleString()}
      </Typography>
      {sub && (
        <Typography sx={{
          fontSize: 9, fontWeight: 700, mt: 0.25, lineHeight: 1.2,
          color: subTone ?? accent,
        }}>
          {sub}
        </Typography>
      )}
    </Box>
  )
}

function SectionRow({
  icon, title, right,
}: { icon?: React.ReactNode; title: string; right?: React.ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mb: 0.75, mt: 0.25 }}>
      {icon}
      <Typography sx={{
        fontSize: 10, fontWeight: 800, letterSpacing: 0.6,
        textTransform: 'uppercase', color: 'text.disabled', flex: 1,
      }}>
        {title}
      </Typography>
      {right}
    </Stack>
  )
}

function InlineEmpty({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.65}
      sx={{ py: 1, mb: 1.25, color: 'text.disabled' }}>
      {icon}
      <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{text}</Typography>
    </Stack>
  )
}

function ChecklistRing({ pct }: { pct: number }) {
  const size = 44
  const stroke = 5
  const radius = (size - stroke) / 2
  const circ = 2 * Math.PI * radius
  const offset = circ - (pct / 100) * circ
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
      <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            stroke="currentColor" strokeWidth={stroke}
            fill="none" opacity={0.08}
            style={{ color: 'inherit' }}
          />
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            stroke="#10B981" strokeWidth={stroke}
            fill="none" strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dashoffset 600ms ease' }}
          />
        </svg>
        <Box sx={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#10B981', lineHeight: 1 }}>
            {pct}%
          </Typography>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.3 }}>
          {pct >= 80 ? 'On track' : pct >= 50 ? 'Catching up' : 'Behind target'}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: 'text.secondary', fontWeight: 600 }}>
          Submission rate this period
        </Typography>
      </Box>
    </Stack>
  )
}

const RANK_BADGES = ['#F59E0B', '#94A3B8', '#B45309', '#64748B', '#64748B'] as const
function PerformerRow({
  performer, rank,
}: { performer: { user_id: string; name: string; avatar_url: string | null; completed_wo: number; completed_checklists: number }; rank: number }) {
  const isTop = rank === 1
  return (
    <Box
      component={motion.div}
      whileHover={{ x: 2 }}
      transition={{ type: 'spring', stiffness: 380, damping: 24 }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.85,
        px: 0.65, py: 0.5, borderRadius: 1.25,
        bgcolor: isTop ? (t) => alpha(t.palette.warning.main, 0.07) : 'transparent',
        '&:hover': {
          bgcolor: (t) => alpha(t.palette.text.primary, 0.04),
        },
      }}>
      <Box sx={{
        width: 20, height: 20, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: RANK_BADGES[rank - 1] ?? '#94A3B8',
        color: 'white', fontSize: 9.5, fontWeight: 800,
        flexShrink: 0,
        boxShadow: isTop ? `0 2px 8px -1px ${alpha(RANK_BADGES[0], 0.55)}` : 'none',
      }}>
        {isTop ? <WorkspacePremiumRoundedIcon sx={{ fontSize: 13 }} /> : rank}
      </Box>
      <UserAvatar name={performer.name} avatarUrl={performer.avatar_url} size={22}
        sx={{ bgcolor: '#0D9488', flexShrink: 0, fontSize: 10 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: 11.5, fontWeight: 700, lineHeight: 1.25,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {performer.name}
        </Typography>
        <Typography sx={{ fontSize: 9.5, color: 'text.disabled', fontWeight: 700,
          letterSpacing: 0.3, textTransform: 'uppercase', lineHeight: 1.3 }}>
          {performer.completed_wo} closed
          {performer.completed_checklists > 0 && ` · ${performer.completed_checklists} cl`}
        </Typography>
      </Box>
    </Box>
  )
}
